import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { HTTPError } from "ky";
import { Eye, EyeOff } from "lucide-react";
import { useLogin, useSession } from "@/hooks/use-auth";
import { useAuthStore } from "@/store/auth-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FullPageSpinner } from "@/components/ui/spinner";

interface LoginSearch {
  redirect?: string;
  next?: string;
}

function safeRedirect(target: string | undefined): string {
  if (!target) return "/";
  if (!target.startsWith("/") || target.startsWith("//")) return "/";
  return target;
}

// Backend-served paths (Django, OAuth, etc.) are not SPA routes — TanStack
// Router's ``navigate({ to })`` would try to resolve them as React routes
// and 404. Detect those upfront and use a full-page navigation instead so
// the OAuth flow on ``/oauth/authorize/?next=...`` can resume cleanly.
function isBackendPath(target: string): boolean {
  return (
    target.startsWith("/oauth/") ||
    target.startsWith("/admin/") ||
    target.startsWith("/api/") ||
    target.startsWith("/.well-known/") ||
    target.startsWith("/invite/")
  );
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  // No synchronous ``beforeLoad`` redirect: the cookie that proves the
  // session is HttpOnly, and ``auth-store`` lives in sessionStorage, so a
  // fresh tab boots with ``isAuthenticated=false`` even when the cookie is
  // valid. Wait for ``useSession`` to revalidate before deciding.
  component: LoginPage,
});

function getErrorMessage(error: unknown): string {
  if (error instanceof HTTPError && error.response.status === 401) {
    return "Credenciales incorrectas. Intenta de nuevo.";
  }
  return "No se pudo iniciar sesión. Intenta más tarde.";
}

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const login = useLogin();
  const session = useSession();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Capture the redirect target from the raw URL on mount. TanStack
  // Router's search canonicalisation drops ``?`` and ``&`` characters
  // that appear *inside* a query value (the OAuth ``next=`` value
  // carries the full authorize URL with its own query string), so by
  // the time ``Route.useSearch()`` returns we may have lost it. Reading
  // ``window.location.search`` once at mount and pulling the param via
  // URLSearchParams preserves the literal value the server sent.
  const [initialTarget] = useState(() => {
    if (typeof window === "undefined") return undefined;
    const params = new URLSearchParams(window.location.search);
    return params.get("next") ?? params.get("redirect") ?? undefined;
  });

  // Bounce out of /login as soon as the cookie revalidates — covers fresh
  // tabs (sessionStorage empty but cookie alive) and explicit hits to /login
  // from an already-authenticated tab. Honour either ``?redirect=`` (SPA
  // convention) or ``?next=`` (Django's default redirect_field_name, used
  // by DOT's authorize-view bounce on /oauth/authorize/).
  useEffect(() => {
    if (!isAuthenticated) return;
    const target = safeRedirect(initialTarget ?? search.redirect ?? search.next);
    if (isBackendPath(target)) {
      window.location.href = target;
    } else {
      navigate({ to: target });
    }
  }, [isAuthenticated, navigate, initialTarget, search.redirect, search.next]);

  if (session.isLoading) return <FullPageSpinner />;

  return (
    <div className="flex min-h-dvh flex-col bg-brand-piedra text-text-primary lg:flex-row">
      <section className="relative flex flex-1 flex-col justify-between overflow-hidden px-8 py-6 md:px-12 md:py-8 lg:min-h-0 lg:px-14 lg:py-10">
        <div className="flex items-center justify-between font-mono text-[11px] tracking-widest text-brand-red lg:text-[13px]">
          <div>● RESERVAS V1.0</div>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <img
            src="/logo-seim-seim.webp"
            alt="Seim·Seim · bar tai"
            className="h-auto w-[260px] select-none md:w-[360px] lg:w-[420px] xl:w-[520px]"
            draggable={false}
          />
        </div>

        <div className="flex items-baseline justify-between border-t border-brand-red/40 pt-6 font-mono text-[11px] uppercase tracking-[0.1em] text-brand-red">
          <span>Chapinero · Bogotá</span>
          <span>Since &apos;26</span>
        </div>
      </section>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate(
            { email, password },
            {
              onSuccess: () => {
                const target = safeRedirect(initialTarget ?? search.redirect ?? search.next);
                if (isBackendPath(target)) {
                  window.location.href = target;
                } else {
                  navigate({ to: target });
                }
              },
            },
          );
        }}
        className="relative flex w-full shrink-0 flex-col justify-center border-t-[1.5px] border-text-primary bg-paper px-8 py-6 text-center text-text-primary md:py-8 lg:w-108 lg:border-t-0 lg:border-l-[1.5px] lg:px-12 lg:py-10 xl:w-120 xl:px-14 xl:py-16"
      >
        <h1 className="mb-2 font-serif text-[42px] font-normal italic leading-none tracking-[-0.02em]">
          ¡Hola otra vez!
        </h1>
        <p className="mb-10 text-sm text-text-secondary xl:mb-14">
          Gestiona reservas, mesas y comensales desde una sola pantalla.
        </p>

        <div className="flex flex-col gap-6">
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-secondary"
            >
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="tu@restaurante.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11 rounded-none border-0 border-b-[1.5px] bg-transparent px-0 text-base focus-visible:border-text-primary focus-visible:ring-0"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-secondary"
            >
              Contraseña
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 rounded-none border-0 border-b-[1.5px] bg-transparent px-0 pr-8 text-base focus-visible:border-text-primary focus-visible:ring-0"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                aria-pressed={showPassword}
                className="absolute -right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center size-11 cursor-pointer rounded-sm text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-text-primary"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {login.isError && (
          <p role="alert" aria-live="polite" className="mt-6 text-sm text-brand-red">
            {getErrorMessage(login.error)}
          </p>
        )}

        <Button type="submit" disabled={login.isPending} size="xl" className="mt-6 w-full xl:mt-8">
          {login.isPending ? "Iniciando sesión..." : "Iniciar sesión"}
        </Button>
      </form>
    </div>
  );
}
